import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Rss } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { ClipFrame } from "@/components/site/ClipFrame";
import { SubscribePanel } from "@/components/logbook/SubscribePanel";
import { useLogbookPosts } from "@/hooks/useLogbook";
import { formatDate } from "@/lib/api";
import { LINKS } from "@/content/site";

const Featured = ({ post }) => (
  <Link to={`/logbook/${post.slug}`} className="group grid gap-8 lg:grid-cols-12 lg:items-end" data-testid="logbook-featured">
    <ClipFrame src={post.image} className="aspect-[16/10] lg:col-span-7" imgClass="transition-transform duration-[1400ms] group-hover:scale-[1.03]" />
    <div className="lg:col-span-5">
      <p className="eyebrow text-signal">Latest entry</p>
      <p className="mono mt-4 text-[12px] uppercase tracking-[0.12em] text-fg-3">{formatDate(post.published)} · {post.reading_minutes} min · {post.author}</p>
      <h2 className="mt-4 text-4xl font-[540] leading-[1.02] tracking-[-0.035em] text-fg transition-colors group-hover:text-signal sm:text-5xl">{post.title}</h2>
      {post.subtitle && <p className="mt-4 text-lg leading-relaxed text-fg-2">{post.subtitle}</p>}
      <span className="mt-6 inline-flex items-center gap-1 text-[15px] text-fg">Read entry <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></span>
    </div>
  </Link>
);

const Index = ({ posts }) => {
  const [hover, setHover] = useState(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 30 });
  const sy = useSpring(y, { stiffness: 260, damping: 30 });
  const onMove = (e) => {
    x.set(e.clientX + 24);
    y.set(e.clientY - 90);
  };
  return (
    <div onMouseMove={onMove} onMouseLeave={() => setHover(null)} className="border-t border-white/10" data-testid="logbook-index">
      {posts.map((p, i) => (
        <Link key={p.slug} to={`/logbook/${p.slug}`} onMouseEnter={() => setHover(p)} className="group grid gap-3 border-b border-white/10 py-6 transition-colors hover:bg-white/[0.015] sm:grid-cols-12 sm:items-baseline" data-testid={`logbook-row-${p.slug}`}>
          <span className="mono text-[12px] uppercase tracking-[0.1em] text-fg-3 sm:col-span-2">{formatDate(p.published)}</span>
          <div className="flex gap-4 sm:col-span-7">
            {p.image && <img src={p.image} alt="" loading="lazy" className="h-16 w-20 shrink-0 rounded-lg object-cover sm:hidden" />}
            <div>
              <p className="text-xl font-[520] leading-snug tracking-[-0.02em] text-fg transition-[color,transform] duration-500 group-hover:translate-x-1 group-hover:text-signal sm:text-2xl">{p.title}</p>
              {p.subtitle && <p className="mt-1 text-[14px] text-fg-2">{p.subtitle}</p>}
            </div>
          </div>
          <span className="eyebrow text-fg-3 sm:col-span-2">{p.categories?.[0] || "Logbook"}</span>
          <span className="mono hidden text-[12px] text-fg-3 sm:col-span-1 sm:block sm:text-right">{String(posts.length - i).padStart(2, "0")}</span>
        </Link>
      ))}
      <motion.div style={{ x: sx, y: sy }} className="pointer-events-none fixed left-0 top-0 z-40 hidden lg:block" aria-hidden="true">
        <motion.div animate={{ opacity: hover?.image ? 1 : 0, scale: hover?.image ? 1 : 0.92 }} transition={{ duration: 0.3 }} className="h-[180px] w-[260px] overflow-hidden rounded-xl border border-white/10 bg-ink-3 shadow-2xl">
          {hover?.image && <img src={hover.image} alt="" className="h-full w-full object-cover" />}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default function Logbook() {
  const { posts, categories, loading, error } = useLogbookPosts();
  const [cat, setCat] = useState("All");
  const list = cat === "All" ? posts : posts.filter((p) => p.categories?.includes(cat));
  const [featured, ...rest] = list;

  return (
    <div data-testid="logbook-page">
      <PageHero
        testId="logbook-hero"
        eyebrow="Writing · Newsletter"
        lines={["Logbook"]}
        text="Hash's newsletter on maritime technology, and the running record of x1025's thinking and progress. Published on Beehiiv; every new entry appears here automatically."
      >
        <Reveal delay={0.45} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[15px]">
          <a href="#subscribe" className="inline-flex items-center gap-1 text-fg hover:text-signal" data-testid="logbook-subscribe-link">Subscribe <ArrowUpRight className="h-4 w-4" /></a>
          <a href={LINKS.rss} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-fg-2 hover:text-signal" data-testid="logbook-rss-link"><Rss className="h-4 w-4" /> RSS feed</a>
          <a href={LINKS.beehiiv} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-fg-2 hover:text-signal" data-testid="logbook-beehiiv-link">Beehiiv archive <ArrowUpRight className="h-4 w-4" /></a>
        </Reveal>
      </PageHero>

      <section className="shell pb-24">
        {loading && !posts.length && <div className="h-[420px] animate-pulse rounded-3xl bg-white/[0.03]" data-testid="logbook-loading" />}
        {error && !posts.length && (
          <div className="rounded-3xl border border-white/[0.08] p-10" data-testid="logbook-error">
            <p className="text-2xl font-[540] text-fg">The Logbook couldn't load just now.</p>
            <a href={LINKS.beehiiv} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-signal">Read it on Beehiiv <ArrowUpRight className="h-4 w-4" /></a>
          </div>
        )}
        {featured && <Featured post={featured} />}
        {categories.length > 1 && (
          <div className="mt-20 flex flex-wrap gap-2" data-testid="logbook-filters">
            {["All", ...categories].map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${cat === c ? "border-signal bg-signal/10 text-fg" : "border-white/15 text-fg-2 hover:text-fg"}`} data-testid={`logbook-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</button>
            ))}
          </div>
        )}
        {rest.length > 0 && <div className="mt-10"><Index posts={rest} /></div>}
      </section>

      <section id="subscribe" className="border-t border-white/[0.06] bg-ink-2 py-24 sm:py-32">
        <div className="shell grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <Reveal><p className="eyebrow text-signal">Subscribe</p></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 text-[2.2rem] font-[540] leading-[1.02] tracking-[-0.035em] text-fg sm:text-5xl">Follow x1025 as it's built.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-lg text-lg leading-relaxed text-fg-2">Insights on autonomous vessels, maritime technology and the industry, from someone who has lived it at sea and on land. No more than once a week.</p></Reveal>
          </div>
          <Reveal className="lg:col-span-5 lg:col-start-8"><SubscribePanel title="Join the Logbook." /></Reveal>
        </div>
      </section>
    </div>
  );
}
